import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const COLLAGE = [
  {
    src: "/family/mountain.jpg",
    alt: "Boys looking out over the mountains above Tegucigalpa",
    span: "col-span-3 sm:col-span-4",
    h: "h-40 sm:h-56",
  },
  {
    src: "/family/trampoline.jpg",
    alt: "Boys jumping on a trampoline in the backyard",
    span: "col-span-3 sm:col-span-2",
    h: "h-40 sm:h-56",
  },
  {
    src: "/family/lego.jpg",
    alt: "Two boys building a Lego set together at the table",
    span: "col-span-1 sm:col-span-2",
    h: "h-28 sm:h-44",
  },
  {
    src: "/family/soccer.jpg",
    alt: "Boys playing football on a neighbourhood court",
    span: "col-span-1 sm:col-span-2",
    h: "h-28 sm:h-44",
  },
  {
    src: "/family/beach.jpg",
    alt: "Four boys standing at the edge of the surf at sunset",
    span: "col-span-1 sm:col-span-2",
    h: "h-28 sm:h-44",
  },
];

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="hairline-grid relative bg-sand py-28 lg:py-40"
    >
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Our Story</Eyebrow>
              <h2 className="font-display mt-7 text-[2.2rem] leading-[1.04] tracking-[-0.02em] text-navy sm:text-5xl">
                Thirteen years of
                <span className="italic text-crimson"> God's faithfulness.</span>
              </h2>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="drop-cap mt-10 text-base leading-[1.85] text-navy/75">
                In 2012 a missions trip to Honduras changed everything. A year later we packed up
                four daughters, a dog, and eleven totes, and God moved our family from Colorado to
                Honduras to care for vulnerable children and the missionaries serving them.
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="mt-7 text-base leading-[1.85] text-navy/70">
                Thirteen years on — through homeschooling, emergency transition homes, foster care,
                and four boys entrusted to us — the season has a new name, but the calling He gave
                us has not changed.
              </p>
            </Reveal>

            <Reveal delay={0.26}>
              <Link
                to="/our-story"
                data-testid="about-read-full-story-btn"
                className="btn-sheen mt-10 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-mono-accent text-[0.68rem] uppercase tracking-[0.2em] text-sand"
              >
                Read Full Story <ArrowRight size={15} />
              </Link>
            </Reveal>

            <Reveal delay={0.32}>
              <div className="mt-12 border-l-2 border-gold pl-6">
                <p className="font-display text-xl italic leading-relaxed text-navy">
                  “And now these three remain: faith, hope and love. But the greatest of these is
                  love.”
                </p>
                <p className="label-eyebrow mt-4 text-sage">1 Corinthians 13:13</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-6">
              <Reveal className="sm:col-span-6" delay={0.1}>
                <div
                  data-testid="about-family-collage"
                  className="grid grid-cols-3 gap-3 sm:grid-cols-6"
                >
                  {COLLAGE.map((img, i) => (
                    <div
                      key={img.src}
                      data-testid={`collage-photo-${i + 1}`}
                      className={`group relative overflow-hidden ${img.span} ${img.h}`}
                    >
                      <motion.img
                        src={img.src}
                        alt={img.alt}
                        loading="lazy"
                        className="h-full w-full scale-[1.02] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.09]"
                      />
                      <div className="absolute inset-0 bg-navy/5 ring-1 ring-inset ring-navy/10 transition-colors duration-700 group-hover:bg-transparent" />
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
